import { useCallback, useEffect, useRef, useState } from 'react';

import { LoadingOutlined, FileTextOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Modal, Progress } from 'antd';

import type { IImportValidationError, IParticipantsImport } from '@/api/endpoints/schools/schoolParticipants';
import {
  useLazyGetSchoolParticipantsImportQuery,
  useLazyGetSchoolParticipantsImportsListQuery,
} from '@/api/endpoints/schools/schoolParticipants';

import styles from './BulkImportModal.module.scss';

type TProps = {
  closeModal: VoidFunction;
  validationImportData: null | { filename: string };
  validationImportErrorData: null | IImportValidationError[];
  importProgressData: null | IParticipantsImport;
};

const ValidationImportContent = ({ data: { filename } }: { data: { filename: string } }) => (
  <>
    <LoadingOutlined style={{ fontSize: '40px' }} />
    <p className={styles.title}>Обработка файла</p>
    <p className={styles.description}>Идет проверка файла...</p>
    <div className={styles['filename-wrapper']}>
      <FileTextOutlined style={{ fontSize: '20px' }} />
      {filename}
    </div>
    <ul className={styles['list-items-wrapper']}>
      {['Проверка структуры файла', 'Валидация данных'].map((text) => (
        <li
          className={styles['list-item']}
          key={text}
        >
          <CheckCircleOutlined style={{ fontSize: '24px', color: 'green' }} />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  </>
);

const ValidationImportErrorContent = ({ errors }: { errors: IImportValidationError[] }) => (
  <>
    <p className={styles.title}>Обнаружены ошибки валидации файла</p>
    <p className={styles.description}>Исправьте указанные ошибки в файле и загрузите его заново</p>
    <div className={styles['filename-wrapper']}>
      <WarningOutlined style={{ fontSize: '20px', color: 'red' }} />
      Обнаружено ошибок: {errors.length}
    </div>
    <ul className={styles['list-items-wrapper']}>
      {errors.map(({ message, context }, index) => (
        <li
          key={message}
          className={styles['list-item']}
        >
          {`${index + 1})`} {message} {context ? `(строка ${context.row})` : ''}
        </li>
      ))}
    </ul>
  </>
);

const ImportProgressContent = ({
  data: {
    file: { filename },
    processed_count,
    school_id: schoolId,
    id: bulkImportId,
  },
}: {
  data: IParticipantsImport;
}) => {
  const [progress, setProgress] = useState(processed_count);
  const [hasImportError, setHasImportError] = useState(false);

  const timeoutId = useRef<null | number>(null);
  const pendingQuery = useRef<{ abort(): void } | null>(null);

  const [getSchoolParticipantsImport] = useLazyGetSchoolParticipantsImportQuery();
  const [getParticipantsImports] = useLazyGetSchoolParticipantsImportsListQuery();

  const clearTimeoutId = useCallback(() => {
    if (typeof window === 'undefined' || !timeoutId.current) {
      return;
    }

    window.clearInterval(timeoutId.current);
    timeoutId.current = null;
  }, []);

  const getProgressData = useCallback(async () => {
    const query = getSchoolParticipantsImport({
      schoolId,
      bulkImportId,
    });
    pendingQuery.current = query;

    try {
      const { processed_count, total_rows, error_count } = await query.unwrap();

      const progress = Math.round(((processed_count + error_count) / total_rows) * 100);
      setProgress(progress);

      if (progress === 100) {
        clearTimeoutId();
        getParticipantsImports({ schoolId: Number(schoolId) });
      }
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') {
        return;
      }

      clearTimeoutId();
      setHasImportError(true);
    }
  }, [clearTimeoutId, schoolId, bulkImportId]);

  useEffect(() => {
    if (typeof window === 'undefined' || timeoutId.current) {
      return;
    }

    timeoutId.current = window.setInterval(() => void getProgressData(), 500);

    return () => {
      clearTimeoutId();
      pendingQuery.current?.abort();
    };
  }, [getProgressData, clearTimeoutId]);

  const content = hasImportError ? (
    <p className={styles.description}>Возникла ошибка при импорте участников. Пожалуйста, повторите операцию позднее</p>
  ) : (
    <>
      <div className={styles['progress-wrapper']}>
        <span>Прогресс:</span>
        <Progress percent={progress} />
      </div>
      <ul className={styles['list-items-wrapper']}>
        {['Формирование отчета'].map((text) => (
          <li
            className={styles['list-item']}
            key={text}
          >
            <CheckCircleOutlined style={{ fontSize: '24px', color: 'green' }} />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </>
  );

  return (
    <>
      <p className={styles.title}>Импорт файла</p>
      <p className={styles.description}>Идет создание учетных записей и зачисление участников...</p>
      <div className={styles['filename-wrapper']}>
        <FileTextOutlined style={{ fontSize: '20px' }} />
        {filename}
      </div>
      {content}
    </>
  );
};

export const BulkImportModal = ({
  validationImportData,
  validationImportErrorData,
  importProgressData,
  closeModal,
}: TProps) => {
  const isModalOpened = Boolean(validationImportData || validationImportErrorData || importProgressData);

  if (!isModalOpened) {
    return null;
  }

  const getContent = () => {
    if (validationImportData) {
      return <ValidationImportContent data={validationImportData} />;
    }

    if (validationImportErrorData) {
      return <ValidationImportErrorContent errors={validationImportErrorData} />;
    }

    if (importProgressData) {
      return <ImportProgressContent data={importProgressData} />;
    }

    return null;
  };

  return (
    <Modal
      width={600}
      open={isModalOpened}
      onCancel={closeModal}
      footer={null}
      centered
    >
      <div className={styles.wrapper}>{getContent()}</div>
    </Modal>
  );
};
